import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { requestV3 } from 'Features/Auth/axios-client'
import { BaseSourceSiteWorkCentre } from 'Features/Projects/Scenarios/EditSettings/EditSettings.types'
import { UpdateSettingsInterface } from './LineSettings/LineSettings.data'

const fetchBaseSourceSiteWorkCentre = async (
  projectId: string,
  scenarioId: string
) => {
  const { data } = await requestV3({
    url: `/base-source-site-workcentre?projectId=${projectId}&scenarioId=${scenarioId}`,
  })
  return data
}

type BaseSourceSiteWorkCentreResponse = { data: BaseSourceSiteWorkCentre[] }

export const useBaseSourceSiteWorkCentre = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}): UseQueryResult<BaseSourceSiteWorkCentre[]> => {
  return useQuery<
    BaseSourceSiteWorkCentreResponse,
    Error,
    BaseSourceSiteWorkCentre[]
  >({
    queryKey: ['base-source-site-workcentre', projectId, scenarioId],
    queryFn: () => fetchBaseSourceSiteWorkCentre(projectId, scenarioId),
    select: (data) => data.data,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
  })
}

const updateBaseSourceSiteWorkCentre = async ({
  changedRows,
  projectId,
  scenarioId,
}: UpdateSettingsInterface<BaseSourceSiteWorkCentre>) => {
  const { data } = await requestV3({
    url: `/base-source-site-workcentre?projectId=${projectId}&scenarioId=${scenarioId}`,
    method: 'PUT',
    data: changedRows,
  })
  return data
}

export const useUpdateBaseSourceSiteWorkCentre = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateBaseSourceSiteWorkCentre,
    onSuccess: (_, variables) => {
      const { projectId, scenarioId } = variables

      queryClient.invalidateQueries({
        queryKey: ['base-source-site-workcentre', projectId, scenarioId],
      })

      onSuccess()
    },
    onError: () => {
      onError()
    },
  })
}

const uploadBaseSourceSiteWorkcentre = async ({
  file,
  projectId,
  scenarioId,
}: {
  file: File
  projectId: string
  scenarioId: string
}) => {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await requestV3({
    url: `/base-source-site-workcentre?projectId=${encodeURIComponent(projectId)}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'POST',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export const useUploadBaseSourceSiteWorkcentre = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: uploadBaseSourceSiteWorkcentre,
    onSuccess: (_, { projectId, scenarioId }) => {
      queryClient.invalidateQueries({
        queryKey: ['base-source-site-workcentre', projectId, scenarioId],
      })
      onSuccess()
    },
    onError,
  })
}
